import React from 'react';

import { calculateDaysRemaining, DaysRemainingInfo } from '../lib/billing-utils';
import { SiteData } from '../lib/api';

interface PlanExpiryAlertProps {
  sites: SiteData[];
}

export function PlanExpiryAlert({ sites }: PlanExpiryAlertProps): JSX.Element | null {
  const [daysRemainingInfo, setDaysRemainingInfo] = React.useState<DaysRemainingInfo | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const checkPlanExpiry = async () => {
      if (!sites || sites.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        // Check the most critical site (earliest expiring)
        const daysInfoPromises = sites.map(site => calculateDaysRemaining(site));
        const allDaysInfo = await Promise.all(daysInfoPromises);
        
        // Find the site with the earliest expiry
        const earliestExpiring = allDaysInfo.reduce((earliest, current) => 
          current.daysRemaining < earliest.daysRemaining ? current : earliest
        );
        
        setDaysRemainingInfo(earliestExpiring);
      } catch (error) {
        console.error('Error checking plan expiry:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkPlanExpiry();
  }, [sites]);

  if (isLoading || !daysRemainingInfo) {
    return null;
  }

  // Only show alert if plan is expired or about to expire
  if (!daysRemainingInfo.isExpired && daysRemainingInfo.daysRemaining > 3) {
    return null;
  }

  const isExpired = daysRemainingInfo.isExpired;
  const alertClass = isExpired 
    ? 'bg-red-50 border-red-200 text-red-800' 
    : 'bg-yellow-50 border-yellow-200 text-yellow-800';
  
  const iconClass = isExpired ? 'text-red-400' : 'text-yellow-400';

  return (
    <div className={`rounded-md border p-4 ${alertClass}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {isExpired ? (
            <svg className={`h-5 w-5 ${iconClass}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className={`h-5 w-5 ${iconClass}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium">
            {isExpired ? 'Metrics Collection Paused' : 'Plan Expiring Soon'}
          </h3>
          <div className="mt-2 text-sm">
            <p>
              {isExpired ? (
                <>
                  Your plan has expired and metrics collection is currently paused to save storage space. 
                  New feedback submissions will not be recorded until you renew your plan.
                </>
              ) : (
                <>
                  Your plan expires in {daysRemainingInfo.daysRemaining} day{daysRemainingInfo.daysRemaining !== 1 ? 's' : ''}. 
                  Metrics collection will be paused after expiry to save storage space.
                </>
              )}
            </p>
          </div>
          <div className="mt-4">
            <div className="-mx-2 -my-1.5 flex">
              <a
                href="/dashboard/billing"
                className={`rounded-md px-2 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isExpired 
                    ? 'bg-red-100 text-red-800 hover:bg-red-200 focus:ring-red-500' 
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:ring-yellow-500'
                }`}
              >
                {isExpired ? 'Renew Plan' : 'Manage Billing'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
